import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useTheme } from "../../theme";

function SearchTags() {
  const { p, d, fonts } = useTheme();
  const [searchParams] = useSearchParams();
  const initialQ = searchParams.get('q') || '';
  const [searchValue, setSearchValue] = useState(initialQ);
  const [results, setResults] = useState([]);
  const [totalTags] = useState(13987);
  const debounceTimer = useRef(null);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (!searchValue.trim()) { setResults([]); return; }

    debounceTimer.current = setTimeout(() => {
      axios
        .get(`${process.env.REACT_APP_API_URL}/tags/search`, {
          params: { q: searchValue.trim() },
        })
        .then((res) => setResults(res.data))
        .catch((err) => console.error("Search error:", err));
    }, 400);

    return () => clearTimeout(debounceTimer.current);
  }, [searchValue]);

  return (
    <div style={{ minHeight: '100%', background: p.bg, color: p.text, fontFamily: fonts.sans }}>
      {/* hero section */}
      <section style={{
        padding: `${d.hero * 0.7}px ${d.pad}px ${d.gap * 2}px`,
        borderBottom: `1px solid ${p.border}`, position: 'relative',
      }}>
        <div aria-hidden style={{
          position: 'absolute', inset: 0,
          backgroundImage: `linear-gradient(${p.borderHi} 1px, transparent 1px), linear-gradient(90deg, ${p.borderHi} 1px, transparent 1px)`,
          backgroundSize: '72px 72px', opacity: .18,
          maskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 75%)',
          pointerEvents: 'none',
        }} />
        <div style={{ maxWidth: 1180, margin: '0 auto', position: 'relative' }}>
          <div style={{
            fontFamily: fonts.mono, fontSize: 11, color: p.textDim,
            letterSpacing: '.18em', textTransform: 'uppercase', marginBottom: 32,
          }}>
            § /search · {totalTags.toLocaleString()} names waiting to be found
          </div>
          <h1 style={{
            fontFamily: fonts.serif, fontSize: d.hero * 0.8, fontWeight: 400,
            letterSpacing: '-.03em', lineHeight: 1, margin: '0 0 16px', color: p.textHi,
          }}>
            <span style={{ fontFamily: fonts.mono, fontWeight: 500, color: p.accent, fontSize: '.75em', letterSpacing: '-.02em' }}>
              {totalTags.toLocaleString()}
            </span> users<br />
            <span style={{ fontStyle: 'italic' }}>waiting to be found.</span>
          </h1>
          <p style={{ fontSize: d.body + 1, lineHeight: 1.55, color: p.text, maxWidth: 560, margin: '0 0 36px' }}>
            Search for usernames across Xbox, PlayStation, Steam, and Discord.
            If anyone has filed a tag for that handle, it'll surface below.
          </p>

          {/* search bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: '4px 4px 4px 24px', background: p.bgPanel,
            border: `1px solid ${p.borderHi}`, maxWidth: 780,
          }}>
            <span style={{ color: p.accent, fontFamily: fonts.mono, fontSize: 18 }}>›</span>
            <input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="type a username to search..."
              style={{
                flex: 1, background: 'transparent', border: 'none', outline: 'none',
                color: p.textHi, fontSize: d.body + 2, padding: '18px 0',
                fontFamily: fonts.mono, caretColor: p.accent,
              }}
            />
            <span style={{
              padding: '14px 24px', background: p.accent, color: p.bg,
              fontFamily: fonts.mono, fontSize: 13, fontWeight: 500, letterSpacing: '.05em',
            }}>SEARCH ↵</span>
          </div>
          <div style={{ marginTop: 14, fontFamily: fonts.mono, fontSize: 11, color: p.textDim, letterSpacing: '.06em' }}>
            FILTERS · <span style={{ color: p.text }}>[any platform]</span> · <span style={{ color: p.text }}>[any year]</span>
          </div>
        </div>
      </section>

      {/* results section */}
      <section style={{ padding: `${d.gap * 3}px ${d.pad}px`, maxWidth: 1180, margin: '0 auto' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 20,
          fontFamily: fonts.mono, fontSize: 11, letterSpacing: '.1em', color: p.textDim, textTransform: 'uppercase',
        }}>
          <span>§ {searchValue.trim() ? `results · ${results.length} records` : 'recent'}</span>
          <span>sort: <span style={{ color: p.text }}>filed_desc</span></span>
        </div>

        {results.length > 0 && (
          <div style={{ border: `1px solid ${p.border}`, background: p.bgPanel }}>
            {results.map((tag, i) => (
              <div key={tag.id || i} style={{
                display: 'grid', gridTemplateColumns: '90px 1fr 2fr 120px',
                gap: 24, alignItems: 'center',
                padding: '18px 24px',
                borderBottom: i < results.length - 1 ? `1px solid ${p.border}` : 'none',
                fontFamily: fonts.mono,
              }}>
                <span style={{ fontSize: 11, letterSpacing: '.1em', color: p.textDim, textTransform: 'uppercase' }}>
                  {tag.findee_account_type || 'UNKNOWN'}
                </span>
                <span style={{ fontSize: 15, color: p.textHi }}>
                  {tag.username}
                </span>
                <span style={{ fontFamily: fonts.serif, fontStyle: 'italic', fontSize: 14, color: p.text, lineHeight: 1.4 }}>
                  {tag.additional_info ? `"${tag.additional_info}"` : '—'}
                </span>
                <span style={{ fontSize: 12, color: p.textDim }}>
                  {tag.date_met || ''}
                </span>
              </div>
            ))}
          </div>
        )}

        {results.length === 0 && searchValue.trim() && (
          <div style={{
            border: `1px solid ${p.border}`, background: p.bgPanel,
            padding: '32px 24px', textAlign: 'center',
            fontFamily: fonts.mono, fontSize: 13, color: p.textDim,
          }}>
            no records match <span style={{ color: p.text }}>"{searchValue}"</span>
          </div>
        )}

        {!searchValue.trim() && (
          <div style={{
            border: `1px solid ${p.border}`, background: p.bgPanel,
            padding: '32px 24px', textAlign: 'center',
            fontFamily: fonts.mono, fontSize: 13, color: p.textDim,
          }}>
            type a username above to begin searching the index
          </div>
        )}
      </section>
    </div>
  );
}

export default SearchTags;
